// Modales bulk-edit pour AllDossiersPage (CC-05 phase b)
// "Modifier type projet" + "Modifier visiteur" pour N dossiers sélectionnés.
import { useEffect, useState } from "react";
import { Loader2, Save, X, Tag, Users, Package } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useConfigList } from "../../hooks/useConfigList";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = (tenantId) => {
  const token = localStorage.getItem("session_token");
  return {
    ...enteteTenant(tenantId),
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

async function _patchDossiers(ids, payload, tenantId) {
  // Bulk PATCH parallèle (pas d'endpoint bulk dédié — N appels concurrents)
  const headers = getHeaders(tenantId);
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await fetch(`${API}/api/metier/dossiers/${id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
          credentials: "include",
        });
        return res.ok;
      } catch {
        return false;
      }
    })
  );
  return results.filter(Boolean).length;
}


export function BulkEditTypeProjetModal({ open, onClose, selectedIds, tenantId, onDone }) {
  const { items: typesProjet = [], loading: loadingTypes } = useConfigList("types_projet", tenantId);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setValue(""); }, [open]);

  const handleSave = async () => {
    if (!value) { toast.error("Sélectionne un type"); return; }
    setSaving(true);
    const ok = await _patchDossiers(selectedIds, { type_projet: value }, tenantId);
    setSaving(false);
    if (ok > 0) toast.success(`${ok}/${selectedIds.length} dossier(s) mis à jour`);
    else toast.error("Aucun dossier mis à jour");
    onDone?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-cyan-400" />
            Modifier le type de projet
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
            Appliquer à <strong>{selectedIds.length}</strong> dossier(s) sélectionné(s).
          </p>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm border"
            style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
            data-testid="bulk-type-projet-select"
            disabled={loadingTypes}
          >
            <option value="">— Sélectionner —</option>
            {typesProjet.map(t => (
              <option key={t.id || t.code || t.label} value={t.label || t.id}>
                {t.label || t.id}
              </option>
            ))}
          </select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}><X className="w-4 h-4 mr-1" />Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !value} className="bg-cyan-600 hover:bg-cyan-700 text-[var(--oh-accent-contrast)]" data-testid="bulk-type-projet-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function BulkEditVisiteurModal({ open, onClose, selectedIds, tenantId, onDone }) {
  const [visiteurs, setVisiteurs] = useState([]);
  const [loadingVis, setLoadingVis] = useState(true);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue("");
    let cancel = false;
    (async () => {
      setLoadingVis(true);
      try {
        const res = await fetch(`${API}/api/metier/lookup/visiteurs`, {
          headers: getHeaders(tenantId), credentials: "include",
        });
        if (res.ok && !cancel) {
          const data = await res.json();
          setVisiteurs(Array.isArray(data) ? data : (data.visiteurs || data.items || []));
        }
      } catch (e) { console.error(e); }
      finally { if (!cancel) setLoadingVis(false); }
    })();
    return () => { cancel = true; };
  }, [open, tenantId]);

  const handleSave = async () => {
    if (!value) { toast.error("Sélectionne un visiteur"); return; }
    setSaving(true);
    const ok = await _patchDossiers(selectedIds, { visiteur_amo: value }, tenantId);
    setSaving(false);
    if (ok > 0) toast.success(`${ok}/${selectedIds.length} dossier(s) mis à jour`);
    else toast.error("Aucun dossier mis à jour");
    onDone?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4 text-cyan-400" />
            Modifier le visiteur AMO
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
            Appliquer à <strong>{selectedIds.length}</strong> dossier(s) sélectionné(s).
          </p>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm border"
            style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
            data-testid="bulk-visiteur-select"
            disabled={loadingVis}
          >
            <option value="">— Sélectionner —</option>
            {visiteurs.map(v => {
              const label = v.nom_complet || v.label || `${v.prenom || ""} ${v.nom || ""}`.trim() || v.email;
              const val = v.nom_complet || v.label || v.nom || v.email || v.id;
              return <option key={v.id || val} value={val}>{label}</option>;
            })}
          </select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}><X className="w-4 h-4 mr-1" />Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !value} className="bg-cyan-600 hover:bg-cyan-700 text-[var(--oh-accent-contrast)]" data-testid="bulk-visiteur-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Action directe (sans modale) : cocher contrat_signe ou mandat_signe.
 *  Retourne le nombre de dossiers patchés (toast côté caller). */
export async function bulkSetSigned(field, ids, tenantId) {
  if (!ids?.length) return 0;
  return _patchDossiers(ids, { [field]: true }, tenantId);
}

/** CC-40 — Setter générique bool : visite_payee / facture_payee /
 *  contrat_signe / mandat_signe / has_devis_installateur / has_photos.
 *  `value` typiquement true (ou false pour décocher). */
export async function bulkSetBoolField(field, value, ids, tenantId) {
  if (!ids?.length) return 0;
  return _patchDossiers(ids, { [field]: !!value }, tenantId);
}

// PN-17 (T-432) — « Cocher Facture payée » passe par LE MÊME geste que la carte de la fiche (PATCH /api/pennylane/update-payment : le paiement de la FACTURE).
// Avant, l'action de masse écrivait ses propres champs sur le dossier (statut_paiement, date_paiement) : un booléen à part que l'état canonique de la facture
// (`paiement_facture.etat_facture_amo`) ne suit pas dès qu'une facture Pennylane est liée. Un dossier sans facture est refusé par le serveur (404) : il n'est pas compté.
export async function bulkSetFacturePayee(ids, tenantId) {
  if (!ids?.length) return 0;
  const headers = getHeaders(tenantId);
  const results = await Promise.all(ids.map(async (id) => {
    try {
      const res = await fetch(`${API}/api/pennylane/update-payment/${id}`, {
        method: "PATCH", headers, credentials: "include", body: JSON.stringify({ payment_status: "paid" }),
      });
      return res.ok;
    } catch { return false; }
  }));
  return results.filter(Boolean).length;
}

/** CC-40 — Set type_projet par bulk PATCH (sans ouvrir la modale qui
 *  attend un select utilisateur — utile quand le caller a déjà la
 *  valeur, ex: shortcut depuis le row context menu). */
export async function bulkSetTypeProjet(value, ids, tenantId) {
  if (!ids?.length || !value) return 0;
  return _patchDossiers(ids, { type_projet: value }, tenantId);
}

// Catalogue legacy : TEF stocke `tva`, climatpac/nouveau modèle stocke
// `taux_tva`. Fallback défensif tant que le backend ne normalise pas
// (TODO: ajouter un serializer Service côté catalogue/routes.py).
const _produitTva = (p) => (p?.taux_tva ?? p?.tva ?? null);

/** Crée N factures Pennylane en batch (decision JJ 2026-05-19). Séquentiel
 *  pour respecter le rate-limit Pennylane. Retourne {success, failed: []}.
 *  Endpoint backend : POST /api/pennylane/create-invoice/{dossier_id}
 *  (fix bug 2026-05-20 — l'ancien POST /invoices retournait 405 Method
 *  Not Allowed, GET only). Le backend gère la validation par dossier
 *  (déjà facturé, devis manquant, etc.) et peut renvoyer 200 avec
 *  {success: false, error: "..."} → on compte ça comme failed. */
export async function bulkCreateInvoices(ids, tenantId) {
  if (!ids?.length) return { success: 0, failed: [] };
  const headers = getHeaders(tenantId);
  const failed = [];
  let success = 0;
  for (const id of ids) {
    try {
      const res = await fetch(`${API}/api/pennylane/create-invoice/${id}`, {
        method: "POST", headers, credentials: "include",
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.success === false) {
          failed.push({ id, error: data.error || "Facture non créée" });
        } else {
          success += 1;
        }
      } else {
        const err = await res.json().catch(() => ({}));
        failed.push({ id, error: err.detail || `HTTP ${res.status}` });
      }
    } catch (e) {
      failed.push({ id, error: e.message || "Erreur réseau" });
    }
  }
  return { success, failed };
}

/** Bulk "Créer un devis" OMV (Option 1, decision JJ 2026-06-08).
 *  Pour chaque dossier : POST /api/metier/devis {dossier_id} → devis brouillon.
 *  Si le dossier a déjà une prime CEE (item.montant_prime_cee > 0), on l'applique
 *  automatiquement via POST /devis/{id}/prime-cee-manuelle (→ net à payer 0,
 *  branding OMV). Sinon brouillon vierge (prime complétée ensuite).
 *  `items` = [{ id, montant_prime_cee, delegataire_nom }]. Séquentiel (création
 *  externe de ressources). Gating omv_france côté appelant.
 */
export async function bulkCreateDevis(items, tenantId) {
  if (!items?.length) return { success: 0, failed: [], primed: 0 };
  const headers = getHeaders(tenantId);
  const failed = [];
  let success = 0;
  let primed = 0;
  for (const it of items) {
    try {
      const res = await fetch(`${API}/api/metier/devis`, {
        method: "POST", headers, credentials: "include",
        body: JSON.stringify({ dossier_id: it.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        failed.push({ id: it.id, error: err.detail || `HTTP ${res.status}` });
        continue;
      }
      const data = await res.json().catch(() => ({}));
      const devisId = data?.devis?.id || data?.id;
      const montant = Number(it.montant_prime_cee);
      if (devisId && montant > 0) {
        const pr = await fetch(`${API}/api/metier/devis/${devisId}/prime-cee-manuelle`, {
          method: "POST", headers, credentials: "include",
          body: JSON.stringify({ montant, delegataire_nom: it.delegataire_nom || null }),
        });
        if (pr.ok) primed += 1;
      }
      success += 1;
    } catch (e) {
      failed.push({ id: it.id, error: e.message || "Erreur réseau" });
    }
  }
  return { success, failed, primed };
}

/** Applique un produit du catalogue AMO sur N dossiers (decision JJ 2026-05-19).
 *  Pas d'input libre : montant_amo + montant_amo_ht + taux_tva +
 *  prestation_amo_ref + prestation_amo_designation copiés depuis le produit
 *  catalogue (source unique de vérité). Aligné CC-19 (prestation_amo_*). */
export async function bulkApplyProduitAmo(produit, ids, tenantId) {
  if (!ids?.length || !produit) return 0;
  return _patchDossiers(
    ids,
    {
      montant_amo: produit.prix_ttc ?? null,
      montant_amo_ht: produit.prix_ht ?? null,
      taux_tva: _produitTva(produit),
      prestation_amo_ref: produit.reference,
      prestation_amo_designation: produit.designation || produit.nom,
    },
    tenantId,
  );
}

export function BulkApplyProduitAmoModal({ open, onClose, selectedIds, tenantId, onDone }) {
  const [services, setServices] = useState([]);
  const [loadingSvc, setLoadingSvc] = useState(true);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue("");
    let cancel = false;
    (async () => {
      setLoadingSvc(true);
      try {
        const res = await fetch(
          `${API}/api/metier/catalogue/services?categorie=prestations&limit=200`,
          { headers: getHeaders(tenantId), credentials: "include" },
        );
        if (res.ok && !cancel) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.services || data.items || []);
          // Filtre AMO : reference commence par "AMO-" OU nom/designation contient "AMO"
          const amoOnly = list.filter(s => {
            const ref = (s.reference || "").toUpperCase();
            const name = (s.designation || s.nom || "").toUpperCase();
            return ref.startsWith("AMO-") || name.includes("AMO");
          });
          setServices(amoOnly);
        }
      } catch (e) { console.error(e); }
      finally { if (!cancel) setLoadingSvc(false); }
    })();
    return () => { cancel = true; };
  }, [open, tenantId]);

  const selected = services.find(s => s.reference === value);

  const handleSave = async () => {
    if (!selected) { toast.error("Sélectionne un produit AMO"); return; }
    setSaving(true);
    const ok = await bulkApplyProduitAmo(selected, selectedIds, tenantId);
    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok}/${selectedIds.length} dossier(s) : ${selected.designation || selected.nom} appliqué`);
      onDone?.();
      onClose();
    } else {
      toast.error("Aucun dossier mis à jour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4" style={{ color: "var(--oh-stat-orange-text)" }} />
            Appliquer un produit AMO
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
            Appliquer à <strong>{selectedIds.length}</strong> dossier(s) sélectionné(s).
            Le montant et le taux TVA seront copiés depuis le catalogue (pas de saisie libre).
          </p>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm border"
            style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
            data-testid="bulk-produit-amo-select"
            disabled={loadingSvc || saving}
            aria-label="Sélectionner un produit AMO"
          >
            <option value="">— Sélectionner un produit AMO —</option>
            {services.map(s => (
              <option key={s.reference} value={s.reference}>
                {s.designation || s.nom} — {s.prix_ttc ?? "?"}€ TTC (TVA {_produitTva(s) ?? 0}%)
              </option>
            ))}
          </select>
          {selected && (
            <div className="rounded-lg border p-3 text-xs space-y-1"
              style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}>
              <div><strong>Référence :</strong> {selected.reference}</div>
              <div><strong>HT :</strong> {selected.prix_ht ?? "?"}€ — <strong>TTC :</strong> {selected.prix_ttc ?? "?"}€ — <strong>TVA :</strong> {_produitTva(selected) ?? 0}%</div>
            </div>
          )}
          {!loadingSvc && services.length === 0 && (
            <p className="text-xs" style={{ color: "var(--oh-stat-amber-text)" }}>
              Aucun produit AMO trouvé dans le catalogue (référence commençant par "AMO-" ou contenant "AMO").
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="w-4 h-4 mr-1" />Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving || !selected}
            className="bg-orange-600 hover:bg-orange-700 text-[var(--oh-accent-contrast)]"
            data-testid="bulk-produit-amo-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
